import React, { useMemo, useState } from "react";
import { T, statusColor, statusFill } from "../theme/tokens.js";
import { Btn, Chip, Empty, Modal, Notice } from "./ui/index.jsx";
import { QuickPick, InlineEdit, inputStyle as input } from "./CellEdit.jsx";
import { columnShapes } from "../lib/trackerViews.js";
import { fmtDate } from "../lib/format.js";

/* The rows of a tracker, as a table you can type into.
 *
 * Read only when it is handed no way to save: a live layer read from the
 * client's GIS draws through the same table as a workbook of our own, and the
 * only difference on screen is that nothing in it can be clicked into.
 */

export default function RowTable({ columns, rows, onEdit, onAdd, onRemove }) {
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(50);
  const [confirm, setConfirm] = useState(null);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => (q
    ? rows.filter((r) => columns.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q)))
    : rows), [rows, columns, q]);

  const shapes = useMemo(() => columnShapes(columns, rows), [columns, rows]);
  const editable = Boolean(onEdit);

  const blank = <span style={{ color: T.line }}>-</span>;

  /* What a value looks like when nobody is editing it. */
  const shownValue = (c, v) => {
    if (v === null || v === undefined || v === "") return blank;
    if (c.type === "date") return fmtDate(v);
    if (c.type === "number") return typeof v === "number" ? v.toLocaleString() : String(v);
    /* A word this app knows as a state is drawn as a state, in the same colour
       it would carry on a register. A workbook's own Status column then reads
       the same as everything else in here. */
    if (shapes[c.key] === "label" && statusFill(v) !== null) {
      return <Chip color={statusColor(v)}>{String(v)}</Chip>;
    }
    if (c.type === "choice") return <Chip>{String(v)}</Chip>;
    return String(v);
  };

  const cell = (c, r) => {
    const v = r[c.key];
    const display = shownValue(c, v);
    /* A locked column is what lines this tracker up with something else, a
       school code against a map layer. Typing over it would not correct
       anything, it would quietly unhook the row from the thing it describes. */
    if (!editable || c.locked) return display;

    if (c.type === "choice") {
      const base = c.options ?? [];
      const options = v && !base.includes(v) ? [v, ...base] : base;
      if (!options.length) return display;
      return (
        <QuickPick current={display} value={v ?? null} options={options}
          renderOption={(o) => shownValue(c, o)}
          onPick={(next) => onEdit(r._id, c.key, next)} />
      );
    }

    return (
      <InlineEdit
        value={v ?? ""}
        type={c.type === "date" ? "date" : c.type === "number" ? "number" : "text"}
        display={display}
        onSave={(next) => onEdit(r._id, c.key,
          c.type === "number" && next !== null ? Number(next) : next)} />
    );
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10,
                    flexWrap: "wrap" }}>
        <input style={{ ...input, flex: "1 1 240px" }} value={query}
          placeholder="Filter these rows"
          onChange={(e) => { setQuery(e.target.value); setLimit(50); }} />
        <span style={{ fontSize: 12.5, color: T.soft, whiteSpace: "nowrap" }}>
          {shown.length.toLocaleString()}
          {shown.length !== rows.length ? ` of ${rows.length.toLocaleString()}` : ""} rows
        </span>
        {onAdd && <Btn small kind="gold" onClick={() => onAdd({})}>Add a row</Btn>}
      </div>

      {rows.length === 0 ? (
        <Empty text={onAdd
          ? "No rows yet. Add one and start typing, or drop a workbook into Import."
          : "No rows yet."} />
      ) : (
      <div style={{ overflowX: "auto", maxHeight: 460, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{
                  position: "sticky", top: 0, background: T.panel,
                  textAlign: c.type === "number" ? "right" : "left", padding: "7px 10px",
                  borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
                  fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                  whiteSpace: "nowrap",
                  opacity: c.locked ? 0.65 : 1,
                  width: shapes[c.key] === "prose" ? undefined : 1,
                }}>{c.label}</th>
              ))}
              {onRemove && <th style={{
                position: "sticky", top: 0, background: T.panel, width: 1,
                borderBottom: `2px solid ${T.navy}`,
              }} />}
            </tr>
          </thead>
          <tbody>
            {shown.slice(0, limit).map((r) => (
              <tr key={r._id ?? r._index} style={{ borderBottom: `1px solid ${T.line}` }}>
                {columns.map((c) => (
                  <td key={c.key} style={{
                    padding: "6px 10px",
                    textAlign: c.type === "number" ? "right" : "left",
                    ...(shapes[c.key] === "prose"
                      ? { whiteSpace: "normal", maxWidth: 320, overflowWrap: "break-word" }
                      : { whiteSpace: "nowrap", width: 1 }),
                  }}>
                    {cell(c, r)}
                  </td>
                ))}
                {onRemove && (
                  <td style={{ padding: "6px 10px", textAlign: "right", width: 1 }}>
                    <Btn small kind="danger" onClick={() => setConfirm(r)}>Remove</Btn>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {shown.length > limit && (
        <div style={{ marginTop: 10 }}>
          <Btn small kind="ghost" onClick={() => setLimit(limit + 200)}>
            Show more, {(shown.length - limit).toLocaleString()} left
          </Btn>
        </div>
      )}

      {editable && rows.length > 0 && (
        <div style={{ fontSize: 12, color: T.soft, marginTop: 10 }}>
          Click any value to change it. It saves as you leave the cell, and
          everyone else sees it the next time their screen loads.
          {columns.some((c) => c.locked)
            ? " The greyed columns are what line these rows up with the map and are left alone."
            : ""}
        </div>
      )}

      {confirm && (
        <Modal title="Remove this row?" onClose={() => setConfirm(null)}>
          <p style={{ fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
            {String(confirm[columns[0]?.key] ?? "This row")} comes off the tracker.
          </p>
          <Notice>
            It is retired rather than destroyed. The row stays in the database and
            can be brought back. Only Heidi can delete anything permanently.
          </Notice>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <Btn kind="ghost" onClick={() => setConfirm(null)}>Keep it</Btn>
            <Btn kind="danger" onClick={async () => {
              await onRemove(confirm._id); setConfirm(null);
            }}>Remove</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
